import { useMemo, useEffect, useRef } from 'preact/hooks';
import type { TransitionBeforePreparationEvent } from 'astro:transitions/client';
import { OfferCardWithOverlays, type Props as OfferCardProps } from 'components/offer/card/OfferCardWithOverlays';
import { OfferSkeleton } from 'components/offer/card/OfferSkeleton';
import { cn } from 'utils/cn.tsx';
import { localize } from 'i18n/localize.ts';
import { user } from 'signals/login.ts';
import { filters } from 'signals/filters';
import { EmptyStateOverlay } from 'components/atoms/EmptyStateOverlay.tsx';
import { useSignals } from 'context/SignalsContext.tsx';
import UnpackedOfferCard, { type Props as UnpackedOfferCardProps } from 'components/offer/card/UnpackedOfferCard';
import { ZboziPlaOfferCard } from 'components/offer/card/ZboziPlaOfferCard';
import { ZboziPromoOfferCard } from 'components/offer/card/ZboziPromoOfferCard';
import { isUnpackedOffer, isZboziPlaOffer, isZboziPromoOffer } from 'utils/isOfferType';
import type { OfferUnion } from 'utils/isOfferType';
import { OfferRowWithOptions, type Props as OfferRowProps } from 'components/offer/rows/OfferRowWithOptions';
import { getOfferId, getUnpackedOfferId } from 'utils/url';
import { TAB_OFFERS_PER_PAGE } from 'utils/constants';
import { useComputed } from '@preact/signals';
import { searchPageAdInserter } from 'components/offer/list/adInserters/searchPageAdInserter.tsx';
import { Icon } from 'components/atoms/Icon';
import { isOfferImageValid } from 'components/offer/utils/images.ts';

export const DEFAULT_GRID_SIZING = 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';
export const SEARCH_GRID_SIZING =
	'grid-cols-2 sm:grid-cols-3 sm:@md:grid-cols-3 sm:@lg:grid-cols-4 sm:@2xl:grid-cols-5 4xl:grid-cols-6';
export const SEARCH_GRID_SIZING_VISIBILITY =
	'[&>*]:hidden [&>*:nth-child(-n+2)]:block sm:[&>*:nth-child(-n+3)]:block sm:@md:[&>*:nth-child(-n+3)]:block sm:@lg:[&>*:nth-child(-n+4)]:block sm:@2xl:[&>*:nth-child(-n+5)]:block 4xl:[&>*:nth-child(n)]:block';
export const OFFER_GRID_SIZING = 'grid-cols-2 sm:grid-cols-3';

const $t = localize();

export type OfferCardPropsWithoutOffer =
	| Partial<Omit<UnpackedOfferCardProps, 'unpackedOffer'>>
	| Partial<Omit<OfferCardProps, 'offer'>>
	| Partial<Omit<OfferRowProps, 'offer'>>;

export type AdInserter = (
	offers: OfferUnion[],
	offersListJSX: JSX.Element[],
	mainOffer?: OfferUnion,
	isMobileBannerAbtest?: boolean,
) => JSX.Element[];

interface Props {
	adInserter?: AdInserter;
	cardSizing?: string;
	className?: string;
	dataTest?: string;
	eagerFirstImage?: boolean;
	emptyStateLoginSourceComponent?: string;
	emptyStateSubtitle?: string;
	emptyStateTitle?: string;
	emptyStateWithSkeletons?: boolean;
	gridSizing?: string;
	isRowView?: boolean;
	noAds?: boolean;
	offerCardProps?: OfferCardPropsWithoutOffer;
	positionOffset?: number;
	showLogoutUserMessage?: boolean;
	skeletonCount?: number;
	mainOffer?: OfferUnion;
	isMobileBannerAbtest?: boolean;
}

export function OfferCardList({
	adInserter = searchPageAdInserter,
	className,
	dataTest,
	eagerFirstImage,
	emptyStateLoginSourceComponent,
	emptyStateSubtitle,
	emptyStateTitle = $t.offerList.emptyStateTitleOffers,
	emptyStateWithSkeletons = true,
	gridSizing = DEFAULT_GRID_SIZING,
	isRowView = false,
	noAds,
	offerCardProps = {},
	positionOffset,
	showLogoutUserMessage,
	skeletonCount,
	mainOffer,
	isMobileBannerAbtest,
}: Props) {
	const limit = useComputed(() => filters.value?.limit); // This prevents re-render when the filters change, trigger it only when the limit itself has changed
	const { offers } = useSignals(['offers']);
	const isLoading = offers?.value.isLoading || false;
	const isAppend = offers?.value.isAppend || false;
	const items = offers?.value.response.results || [];
	const showIsLogoutMessage = showLogoutUserMessage && !isLoading && user.value.state !== 'login';
	const showEmptyOverlay = !isLoading && !items.length;
	const itemsPerPage = skeletonCount || limit.value || TAB_OFFERS_PER_PAGE;

	const listContent = useMemo(() => {
		if ((emptyStateTitle && showEmptyOverlay && !filters.value.hledej.seoName) || showIsLogoutMessage) {
			return [
				...(emptyStateWithSkeletons ? computeSkeletons(itemsPerPage, false, isRowView) : []),
				<EmptyStateOverlay
					key="emptyState"
					className={cn(
						!emptyStateWithSkeletons &&
							'md:shadow-none md:transform-none md:inline-block md:inset-auto md:mx-auto md:static',
					)}
					title={emptyStateTitle}
					subtitle={emptyStateSubtitle}
					showLoginButton={showIsLogoutMessage}
					loginOptions={{ sourceComponent: emptyStateLoginSourceComponent }}
				/>,
			];
		} else if (isLoading && !isAppend) {
			return computeSkeletons(itemsPerPage, true, isRowView);
		} else if (isLoading && isAppend) {
			return computeOfferList(
				adInserter,
				items,
				noAds,
				offerCardProps,
				isRowView,
				positionOffset,
				eagerFirstImage,
				mainOffer,
				isMobileBannerAbtest,
			).concat(computeSkeletons(itemsPerPage, true, isRowView));
		} else {
			return computeOfferList(
				adInserter,
				items,
				noAds,
				offerCardProps,
				isRowView,
				positionOffset,
				eagerFirstImage,
				mainOffer,
				isMobileBannerAbtest,
			);
		}
	}, [
		emptyStateTitle,
		emptyStateSubtitle,
		emptyStateLoginSourceComponent,
		showEmptyOverlay,
		showIsLogoutMessage,
		isLoading,
		isAppend,
		items,
		itemsPerPage,
		noAds,
		offerCardProps,
		positionOffset,
		isRowView,
		isMobileBannerAbtest,
	]);

	const offerIdRef = useRef<string>();

	useEffect(() => {
		const restoreScroll = () => {
			const id = offerIdRef.current;
			if (!id) return;

			const offerElement = document.querySelector(`[data-offer-id="${id}"]`);
			if (offerElement) {
				const observer = new IntersectionObserver((entries) => {
					if (entries[0]?.isIntersecting) {
						observer.disconnect();
					} else {
						offerElement.scrollIntoView();
						observer.disconnect();
					}
				});
				observer.observe(offerElement);
			}
		};
		const getOfferIdFromHistory = (event: TransitionBeforePreparationEvent) => {
			const lastPageType = window.historyStorage.getLastPageType();
			if (event.direction === 'back' && lastPageType === 'advertDetail') {
				const seoName = event.from.pathname.split('/').pop();
				if (seoName) {
					let offerId;
					let unpackedOfferId;

					try {
						offerId = getOfferId(seoName);
					} catch {
						offerId = undefined;
					}
					try {
						unpackedOfferId = getUnpackedOfferId(seoName);
					} catch {
						unpackedOfferId = undefined;
					}

					if (!offerId && !unpackedOfferId) {
						offerIdRef.current = undefined;
						throw new Error(`Invalid offerId/unpackedOfferId from seoName: ${seoName}`);
					}

					offerIdRef.current = offerId || unpackedOfferId;
				} else {
					offerIdRef.current = undefined;
				}
			} else {
				offerIdRef.current = undefined;
			}
		};

		globalThis.document.addEventListener('astro:before-preparation', getOfferIdFromHistory);
		globalThis.document.addEventListener('astro:after-swap', restoreScroll);

		return () => {
			globalThis.document.removeEventListener('astro:before-preparation', getOfferIdFromHistory);
			globalThis.document.removeEventListener('astro:after-swap', restoreScroll);
		};
	});

	return (
		<div class="@container" data-test={dataTest}>
			{emptyStateTitle && showEmptyOverlay && filters.value.hledej.seoName ? (
				<div>
					<p className="text-lg mb-2">
						{$t.offerList.emptyStateTitleOffersWithSearch}{' '}
						<span className="font-bold">"{filters.value.hledej.seoName}"</span>
					</p>
					<p className="inline-flex gap-2 text-dark-blue-50">
						<Icon type="lightbulb" /> {$t.offerList.emptyStateHintOffersWithSearch}
					</p>
				</div>
			) : (
				<ul
					class={cn(
						'grid gap-[24px_12px] relative',
						{
							'overflow-hidden md:grid-rows-none': isLoading && !isAppend,
							'overflow-hidden grid-rows-[1fr_repeat(20,0)] [&>*:first-child]:mb-6 md:grid-rows-[1fr_1fr_repeat(20,0)] gap-y-0 [&>li]:hidden md:[&>li]:block grid-cols-1':
								!isRowView && (showEmptyOverlay || showIsLogoutMessage),
						},
						gridSizing,
						isRowView && 'grid-cols-1 gap-y-4 grid-rows-1',
						isRowView &&
							emptyStateWithSkeletons &&
							(showEmptyOverlay || showIsLogoutMessage) &&
							'overflow-hidden grid-rows-none [&>li]:hidden md:[&>li]:block md:[&>li:nth-child(n+6)]:hidden',
						!emptyStateWithSkeletons &&
							(showEmptyOverlay || showIsLogoutMessage) &&
							'md:grid-rows-[1fr_repeat(20,0)] grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-1',
						className,
					)}
					data-test="offer-list"
				>
					{listContent}
				</ul>
			)}
		</div>
	);
}

function computeOfferList(
	adInserter: AdInserter | undefined,
	offers: OfferUnion[],
	noAds: boolean = false,
	offerCardProps: OfferCardPropsWithoutOffer = {},
	isRowsOrCards: boolean,
	positionOffset: number = 0,
	eagerFirstImage: boolean = false,
	mainOffer?: OfferUnion,
	isMobileBannerAbtest?: boolean,
) {
	if (!offers) return [];

	let lazy = !eagerFirstImage;

	const newOfferListJsx = offers.map((offer, index) => {
		const JSX = (
			// Start the position count from 1 instead of 0
			<li key={offer.id} data-offer-id={offer.id}>
				{isUnpackedOffer(offer) ? (
					<UnpackedOfferCard
						key={offer.id}
						analyticsPosition={`${index + positionOffset + 1}`}
						lazyImage={lazy}
						unpackedOffer={offer}
						{...offerCardProps}
					/>
				) : isZboziPlaOffer(offer) ? (
					<ZboziPlaOfferCard key={offer.id} zboziOffer={offer} lazyImage={lazy} />
				) : isZboziPromoOffer(offer) ? (
					<ZboziPromoOfferCard key={offer.id} zboziOffer={offer} lazyImage={lazy} />
				) : isRowsOrCards ? (
					<OfferRowWithOptions
						key={offer.id}
						analyticsPosition={`${index + positionOffset + 1}`}
						lazyImage={lazy}
						offer={offer}
						{...offerCardProps}
					/>
				) : (
					<OfferCardWithOverlays
						key={offer.id}
						analyticsPosition={`${index + positionOffset + 1}`}
						lazyImage={lazy}
						offer={offer}
						{...offerCardProps}
					/>
				)}
			</li>
		);

		if (!lazy && isOfferImageValid(offer)) {
			lazy = true;
		}

		return JSX;
	});

	if (noAds || !adInserter) return newOfferListJsx;

	return adInserter(offers, newOfferListJsx, mainOffer, isMobileBannerAbtest);
}

function computeSkeletons(count: number, animate?: boolean, isRowsOrCards?: boolean) {
	const newOfferListJsx = Array(count)
		.fill(0)
		.map((_, index) => (
			<li key={`skeleton-${index}`}>
				<OfferSkeleton animate={animate} isRow={isRowsOrCards} />
			</li>
		));

	return newOfferListJsx;
}
